import { useRegisterByEmailMutation } from '@generated/graphql';
import { Button, Stack } from '@mui/material';
import { Form, TextFieldControl, useToast, VALIDATION } from '@myhrbox/myhrbox-frontend-core';
import PasswordStrength from 'components/PasswordStrength';
import PhoneNumberFieldControl from 'components/form/FieldControls/PhoneNumberFieldControl';
import AgreementCheckbox from 'pages/onboarding/ConfirmData/components/AgreementCheckbox';
import useHandleLogin from 'pages/onboarding/Login/hooks/useHandleLogin';
import { authFieldSx } from 'pages/onboarding/styles';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { isPasswordValid } from '../../components/PasswordStrength/utils';
import { RegisterFormData } from './types';

type RegisterFormProps = {
  onSuccess?: () => void;
  form: UseFormReturn<RegisterFormData>;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const RegisterForm = ({ onSuccess, form }: RegisterFormProps) => {
  const { t } = useTranslation();
  const { showErrorToast, showSuccessToast } = useToast();
  const { handleSubmit } = form;
  const password = useWatch({
    control: form.control,
    name: 'password',
    defaultValue: '',
  });
  const [registerByEmail, { loading }] = useRegisterByEmailMutation();
  const handleLogin = useHandleLogin();

  // TODO: for pipeline

  const onSubmit = ({ confirmPassword, confirmAgreement, ...formData }: RegisterFormData) => {
    registerByEmail({
      variables: {
        input: formData,
      },
    })
      .then((res) => {
        const accessToken = res.data?.registerByEmail.accessToken;
        const refreshToken = res.data?.registerByEmail.refreshToken;

        if (accessToken && refreshToken) {
          showSuccessToast(t('register.submittedMessage', { email: formData.email }), {
            snackbarProps: { autoHideDuration: 10000 },
          });
          handleLogin(accessToken, refreshToken);
        } else {
          showErrorToast(t('errors.common'));
        }
      })
      .catch(() => {
        showErrorToast(t('errors.common'));
      });
  };

  return (
    <Form form={form}>
      <Stack spacing={3}>
        <TextFieldControl
          name='fullName'
          label={t('user.fullNameLabel')}
          placeholder={t('register.fullNamePlaceholder')}
          type='text'
          rules={{
            required: true,
            ...VALIDATION({
              message: t('validation.userName'),
              maxLengthMessage: (count) => t('validation.maxLengthExceeded', { count }),
            }).userName,
          }}
          disabled={loading}
          sx={authFieldSx}
        />
        <TextFieldControl
          name='email'
          label='Email'
          placeholder='you@company.ru'
          type='email'
          rules={{
            required: true,
            ...VALIDATION({
              message: t('validation.invalidEmailCharacters'),
              maxLengthMessage: (count) => t('validation.maxLengthExceeded', { count }),
            }).emailFull,
          }}
          disabled={loading}
          sx={authFieldSx}
        />
        <Stack spacing={1}>
          <TextFieldControl
            name='password'
            label={t('password')}
            placeholder={t('register.passwordPlaceholder')}
            type='password'
            rules={{
              required: true,
              validate: (value) => isPasswordValid(value) || t('register.passwordHint'),
            }}
            disabled={loading}
            sx={authFieldSx}
          />
          <PasswordStrength password={password} />
        </Stack>
        <TextFieldControl
          name='confirmPassword'
          label={t('register.repeatPassword')}
          placeholder={t('register.repeatPassword')}
          type='password'
          rules={{
            required: true,
            validate: (value) =>
              value === form.getValues('password') || t('validation.passwordsMustMatch'),
          }}
          disabled={loading}
          sx={authFieldSx}
        />
        <PhoneNumberFieldControl
          disabled={loading}
          placeholder={t('register.phoneNumberPlaceholder')}
          sx={authFieldSx}
        />
        <AgreementCheckbox />

        <Button
          variant='contained'
          size='large'
          fullWidth
          loading={loading}
          onClick={handleSubmit(onSubmit)}>
          {t('next')}
        </Button>
      </Stack>
    </Form>
  );
};

export default RegisterForm;
